/*******************************************************************************
 * ADOBE CONFIDENTIAL
 * ___________________
 *
 * Copyright 2026 Adobe
 * All Rights Reserved.
 *
 * NOTICE: All information contained herein is, and remains
 * the property of Adobe and its suppliers, if any. The intellectual
 * and technical concepts contained herein are proprietary to Adobe
 * and its suppliers and are protected by all applicable intellectual
 * property laws, including trade secret and copyright laws.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Adobe.
 ******************************************************************************/
import { mapFromRailsDirectUploadErrorMessage } from "utilities/mapFromRailsDirectUploadErrorMessage";

declare global {
  interface Window {
    unloaded: boolean;
  }
}

if (typeof window !== "undefined") {
  window.unloaded = false;
  const unloadHandler = (): void => {
    window.unloaded = true;
  };

  window.addEventListener("beforeunload", unloadHandler);
}

export class NewRelicDecoratedError extends Error {
  customAttributes: Record<string, newrelic.SimpleType>;
  type: string;
  constructor(
    error: Error | string,
    customAttributes: Record<string, newrelic.SimpleType> = {}
  ) {
    const messageParts = [];
    if (typeof error === "string") {
      messageParts.push(mapFromRailsDirectUploadErrorMessage(error));
    } else {
      messageParts.push(error.message);
    }
    super(messageParts.filter((part) => part).join(": "));
    if (typeof error !== "string") {
      this.name = error.name;
      this.stack = error.stack;
    }
    this.customAttributes = customAttributes;
  }
}

export function noticeError(
  error: NewRelicDecoratedError | Error | string,
  customAttributes?: Record<string, newrelic.SimpleType>
): void {
  const nrError =
    error instanceof NewRelicDecoratedError
      ? error
      : new NewRelicDecoratedError(error, customAttributes);

  const attributes = {
    ...nrError.customAttributes,
    unloaded: String(window.unloaded),
  };
  newrelic.noticeError(nrError, attributes);
}
